import {
  lazy,
  type ReactNode,
  type SetStateAction,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { ShellConversation } from "@/app/workspace/components/ShellConversation";
import { ShellDock } from "@/app/workspace/components/ShellDock";
import { ShellSideMenu } from "@/app/workspace/components/ShellSideMenu";
import { useSession } from "@/features/auth/hooks/use-session";
import { introSeen } from "@/features/intro/lib/intro-seen";
import { prefetchStageList } from "@/features/order-instructions/lib/stage-list";
import type { DockEntry } from "@/app/workspace/dock";
import {
  closedRecordOf,
  forgetPortalRecord,
  isPortalWorkResume,
  RESUME_PORTAL_WORK,
} from "@/features/advice-portal/lib/portal-work";
import {
  loadLastDockApplicationPath,
  persistLastDockApplicationPath,
} from "@/app/workspace/dock-page-preference";
import {
  loadConversationFolded,
  loadConversationWidth,
  loadDockFolded,
  loadSideCollapsed,
  persistConversationFolded,
  persistConversationWidth,
  persistDockFolded,
  persistSideCollapsed,
} from "@/app/workspace/layout-preferences";
import { useTranslation } from "@/shared/i18n/LocaleProvider";
import { clientPath, DOCK_PATHS, isRecordPaneRoute } from "@/shared/ui/dock-pages";
import { Clock } from "@/shared/ui/layout/Clock";
import { HomeSlot } from "@/shared/ui/layout/HomeSlot";
import { Splitter } from "@/shared/ui/layout/Splitter";
import { WorkArea } from "@/shared/ui/layout/WorkArea";
import { type WorkHeadHosts, WorkHeadSlotProvider } from "@/shared/ui/layout/WorkHeadSlot";
import { ToastHost } from "@/shared/ui/overlays/ToastHost";
import {
  type ChatContext,
  type ComposerSubmit,
  type IntroRequest,
  type SimonPanelView,
  type WorkspaceContextValue,
  WorkspaceProvider,
  type WorkspaceRightPanel,
} from "@/shared/ui/WorkspaceContext";

export interface ShellRouteHandle {
  titleKey?: string;
  conversationOnly?: boolean;
  split?: boolean;
  keepMountedAcrossSegments?: boolean;
}

export function AppShell({
  route: handle = NO_HANDLE,
  children,
}: {
  route?: ShellRouteHandle;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { pathname, search, state: routeState } = useLocation();
  const navigate = useNavigate();
  const home = pathname === DOCK_PATHS.home;
  const conversationRoute = home || handle.conversationOnly === true;
  const view = viewOf(pathname);
  const portalRoute = isRecordPaneRoute(pathname);

  const [sideCollapsed, setSideCollapsed] = useState(loadSideCollapsed);
  const [foldedViews, setFoldedViews] = useState<Readonly<Record<string, boolean>>>({});
  const [workCollapsedView, setWorkCollapsedView] = useState<string | null>(null);
  const [simonPanelView, setSimonPanelView] = useState<SimonPanelView>("conversation");
  const [convWidth, setConvWidth] = useState(loadConversationWidth);
  const [dockFolded, setDockFolded] = useState(loadDockFolded);
  const [homeSlotVisible, setHomeSlotVisible] = useState(true);
  const [rightPanel, setRightPanel] = useState<Keyed<WorkspaceRightPanel> | null>(null);
  const [panelFullScreen, setPanelFullScreen] = useState(false);
  const [chatContext, setChatContextState] = useState<Keyed<ChatContext> | null>(null);
  const [pendingComposerDraft, setPendingComposerDraft] = useState<string | null>(null);
  const consumeComposerDraft = useCallback(() => setPendingComposerDraft(null), []);
  useEffect(() => {
    if (pendingComposerDraft === null) return;
    const expiry = window.setTimeout(() => setPendingComposerDraft(null), COMPOSER_PICKUP_MS);
    return () => window.clearTimeout(expiry);
  }, [pendingComposerDraft]);
  const { intro, showIntro, reportIntroShown, closeIntro } =
    useFirstRunIntro(setPendingComposerDraft);

  const storedFold = useMemo(() => loadConversationFolded(view), [view]);
  const convCollapsed = foldedViews[view] ?? storedFold ?? handle.split === true;
  const workCollapsed = conversationRoute || workCollapsedView === view;
  const convMini = !conversationRoute && !workCollapsed && convCollapsed;

  const foldConversation = useCallback((target: string, folded: boolean) => {
    persistConversationFolded(target, folded);
    setFoldedViews((current) =>
      current[target] === folded ? current : { ...current, [target]: folded },
    );
  }, []);

  useEffect(() => {
    if (!conversationRoute) persistLastDockApplicationPath(pathname);
  }, [conversationRoute, pathname]);

  useEffect(() => prefetchStageList(), []);

  useEffect(() => {
    const closed = closedRecordOf(routeState);
    if (closed !== null) forgetPortalRecord(closed);
    if (closed !== null || isPortalWorkResume(routeState)) {
      navigate(`${pathname}${search}`, { replace: true, state: null });
    }
  }, [routeState, navigate, pathname, search]);

  const lastApplicationPath = useCallback(
    () => loadLastDockApplicationPath() ?? DOCK_PATHS.clients,
    [],
  );

  const toggleSide = useCallback(() => {
    setSideCollapsed((collapsed) => {
      persistSideCollapsed(!collapsed);
      return !collapsed;
    });
  }, []);

  const toggleWork = useCallback(() => {
    if (conversationRoute) {
      navigate(lastApplicationPath());
      return;
    }
    if (workCollapsed) {
      setWorkCollapsedView(null);
      return;
    }
    setWorkCollapsedView(view);
    foldConversation(view, false);
  }, [conversationRoute, foldConversation, lastApplicationPath, navigate, view, workCollapsed]);

  const toggleConversation = useCallback(() => {
    if (convMini) {
      foldConversation(view, false);
      return;
    }
    if (conversationRoute) {
      const target = lastApplicationPath();
      foldConversation(viewOf(target), true);
      navigate(target);
      return;
    }
    foldConversation(view, true);
    if (workCollapsed) setWorkCollapsedView(null);
  }, [
    convMini,
    conversationRoute,
    foldConversation,
    lastApplicationPath,
    navigate,
    view,
    workCollapsed,
  ]);

  const showChat = useCallback(
    (alongsideApplication = false) => {
      foldConversation(view, false);
      if (alongsideApplication || conversationRoute) return;
      setWorkCollapsedView(view);
    },
    [conversationRoute, foldConversation, view],
  );

  const showApplication = useCallback(
    (path?: string, alongsideChat?: boolean) => {
      const target = path ?? lastApplicationPath();
      setWorkCollapsedView(null);
      if (alongsideChat !== undefined) foldConversation(viewOf(target), !alongsideChat);
      if (target !== pathname) navigate(target);
    },
    [foldConversation, lastApplicationPath, navigate, pathname],
  );

  const onDockSelect = useCallback(
    (entry: DockEntry) => {
      if (entry.path === DOCK_PATHS.home) {
        setSimonPanelView("conversation");
        setHomeSlotVisible(true);
        if (!home) navigate(DOCK_PATHS.home);
        return;
      }
      if (entry.resume) {
        const target = entry.resume() ?? entry.path;
        setWorkCollapsedView(null);
        if (target !== `${pathname}${search}`) navigate(target, { state: RESUME_PORTAL_WORK });
        return;
      }
      showApplication(entry.path);
    },
    [home, navigate, showApplication, pathname, search],
  );

  const showSimonConversation = useCallback(() => setSimonPanelView("conversation"), []);
  const showAllChats = useCallback(() => {
    setSimonPanelView("all-chats");
    foldConversation(view, false);
  }, [foldConversation, view]);
  const openClientProfile = useCallback(
    (clientId: string) => showApplication(clientPath(clientId)),
    [showApplication],
  );

  const setLeftPanelWidth = useCallback((next: SetStateAction<number>) => {
    setConvWidth((current) => {
      const value = typeof next === "function" ? next(current ?? 0) : next;
      persistConversationWidth(value);
      return value;
    });
  }, []);

  const [pendingComposerSubmit, setPendingComposerSubmit] = useState<ComposerSubmit | null>(null);
  const requestComposerSubmit = useCallback(
    (request: ComposerSubmit) => setPendingComposerSubmit(request),
    [],
  );
  const consumeComposerSubmit = useCallback(() => setPendingComposerSubmit(null), []);
  useEffect(() => {
    if (pendingComposerSubmit === null) return;
    const request = pendingComposerSubmit;
    const expiry = window.setTimeout(() => {
      setPendingComposerSubmit((current) => (current === request ? null : current));
      request.onSettled?.({ sent: false, reason: "not-picked-up" });
    }, COMPOSER_PICKUP_MS);
    return () => window.clearTimeout(expiry);
  }, [pendingComposerSubmit]);

  const [createClientOpen, setCreateClientOpen] = useState(false);
  const [createClientPanelRequested, setCreateClientPanelRequested] = useState(false);
  const [createClientPrefill, setCreateClientPrefill] = useState<Record<string, unknown> | null>(
    null,
  );
  const openCreateClient = useCallback(() => {
    setCreateClientPrefill(null);
    setCreateClientPanelRequested(true);
    setCreateClientOpen(true);
  }, []);
  const createClientOpenRef = useRef(createClientOpen);
  createClientOpenRef.current = createClientOpen;
  const openCreateClientPrefilled = useCallback((prefill: Record<string, unknown>): boolean => {
    if (createClientOpenRef.current) return false;
    setCreateClientPrefill(prefill);
    setCreateClientPanelRequested(true);
    setCreateClientOpen(true);
    return true;
  }, []);
  const closeCreateClient = useCallback(() => setCreateClientOpen(false), []);

  const openRightPanel = useCallback(
    (panel: WorkspaceRightPanel) => {
      setRightPanel({ pathname, value: panel });
      setPanelFullScreen(false);
    },
    [pathname],
  );
  const closeRightPanel = useCallback(() => {
    setRightPanel(null);
    setPanelFullScreen(false);
  }, []);
  const setChatContext = useCallback(
    (context: ChatContext | null) =>
      setChatContextState(context ? { pathname, value: context } : null),
    [pathname],
  );
  const shownPanel = rightPanel?.pathname === pathname ? rightPanel.value : null;
  const shownChatContext = chatContext?.pathname === pathname ? chatContext.value : null;

  const ctx: WorkspaceContextValue = useMemo(
    () => ({
      layoutMode: workCollapsed ? "chat" : convMini ? "application" : "split",
      chatVisible: !convMini,
      applicationVisible: !workCollapsed,
      applicationAvailable: true,
      systemPage: false,
      showChat,
      showApplication,
      toggleChatPanel: toggleConversation,
      toggleApplicationPanel: toggleWork,
      simonPanelView,
      showSimonConversation,
      showAllChats,
      openClientProfile,
      leftPanelWidth: convWidth ?? 0,
      setLeftPanelWidth,
      pendingComposerSubmit,
      requestComposerSubmit,
      consumeComposerSubmit,
      pendingComposerDraft,
      requestComposerDraft: setPendingComposerDraft,
      consumeComposerDraft,
      showIntro,
      openCreateClient,
      openCreateClientPrefilled,
      openRightPanel,
      closeRightPanel,
      chatContext: shownChatContext,
      setChatContext,
      setHomeSlotVisible,
    }),
    [
      workCollapsed,
      convMini,
      showChat,
      showApplication,
      toggleConversation,
      toggleWork,
      simonPanelView,
      showSimonConversation,
      showAllChats,
      openClientProfile,
      convWidth,
      setLeftPanelWidth,
      pendingComposerSubmit,
      requestComposerSubmit,
      consumeComposerSubmit,
      pendingComposerDraft,
      consumeComposerDraft,
      showIntro,
      openCreateClient,
      openCreateClientPrefilled,
      openRightPanel,
      closeRightPanel,
      shownChatContext,
      setChatContext,
    ],
  );

  const [hosts, setHosts] = useState<WorkHeadHosts>(NO_HOSTS);
  const hostRefs = useMemo(
    () => ({
      title: (element: HTMLElement | null) =>
        setHosts((current) =>
          current.title === element ? current : { ...current, title: element },
        ),
      actions: (element: HTMLElement | null) =>
        setHosts((current) =>
          current.actions === element ? current : { ...current, actions: element },
        ),
      tabs: (element: HTMLElement | null) =>
        setHosts((current) => (current.tabs === element ? current : { ...current, tabs: element })),
      dialog: (element: HTMLElement | null) =>
        setHosts((current) =>
          current.dialog === element ? current : { ...current, dialog: element },
        ),
    }),
    [],
  );
  const [titleClaims, setTitleClaims] = useState(0);
  const [dialogClaims, setDialogClaims] = useState(0);

  const bodyRef = useRef<HTMLDivElement>(null);
  const [convElement, setConvElement] = useState<HTMLElement | null>(null);
  const convRef = useRef<HTMLElement | null>(null);
  convRef.current = convElement;

  const homeSlot =
    home && homeSlotVisible ? (
      <HomeSlot label={t("shell.home.region")}>{children}</HomeSlot>
    ) : undefined;

  return (
    <WorkspaceProvider value={ctx}>
      <WorkHeadSlotProvider
        hosts={hosts}
        onTitleClaims={setTitleClaims}
        onDialogClaims={setDialogClaims}
        applicationVisible={!workCollapsed}
      >
        <div
          data-dock={dockFolded ? "folded" : "open"}
          inert={intro !== null}
          className="relative flex h-full min-h-0 flex-col"
        >
          <div ref={bodyRef} className="flex min-h-0 flex-1">
            <ShellSideMenu
              collapsed={sideCollapsed}
              onToggle={toggleSide}
              onAllChats={showAllChats}
            />
            <ShellConversation
              columnRef={setConvElement}
              collapsed={convMini}
              mode={home ? "home" : workCollapsed ? "wide" : "beside"}
              onToggle={toggleConversation}
              view={simonPanelView}
              width={convWidth}
              homeSlot={homeSlot}
              hiddenRoute={conversationRoute && !home ? children : undefined}
              showClock={!portalRoute}
            />
            {!convMini && !workCollapsed && (
              <Splitter
                label={t("shell.conv.resize")}
                targetRef={convRef}
                containerRef={bodyRef}
                width={convWidth}
                onChange={(width, done) => {
                  setConvWidth(width);
                  if (done) persistConversationWidth(width);
                }}
                onReset={() => {
                  setConvWidth(null);
                  persistConversationWidth(null);
                }}
              />
            )}
            <WorkArea
              collapsed={workCollapsed}
              onToggle={toggleWork}
              labels={{
                open: t("shell.work.open"),
                collapse: t("shell.work.collapse"),
                region: t("shell.work.region"),
              }}
              title={handle.titleKey ? t(handle.titleKey) : null}
              titleClaimed={titleClaims > 0}
              when={portalRoute ? <Clock align="end" /> : undefined}
              hostRefs={hostRefs}
              dialogHosted={dialogClaims > 0}
              panel={
                shownPanel
                  ? {
                      content: shownPanel,
                      fullScreen: panelFullScreen,
                      onToggleFullScreen: () => setPanelFullScreen((value) => !value),
                      onClose: () => {
                        shownPanel.onClose?.();
                        closeRightPanel();
                      },
                    }
                  : null
              }
            >
              {conversationRoute ? null : children}
            </WorkArea>
          </div>
          <ShellDock
            folded={dockFolded && intro === null}
            onSelect={onDockSelect}
            onFold={() => {
              setDockFolded(true);
              persistDockFolded(true);
            }}
            onUnfold={() => {
              setDockFolded(false);
              persistDockFolded(false);
            }}
          />
        </div>
        <ToastHost />
        {intro && (
          <Suspense fallback={null}>
            <IntroOverlay
              key={intro.key}
              request={intro.request}
              onShown={reportIntroShown}
              onClose={closeIntro}
            />
          </Suspense>
        )}
        {createClientPanelRequested && (
          <Suspense fallback={null}>
            <CreateClientPanel
              open={createClientOpen}
              onClose={closeCreateClient}
              prefill={createClientPrefill}
            />
          </Suspense>
        )}
      </WorkHeadSlotProvider>
    </WorkspaceProvider>
  );
}

function useFirstRunIntro(onDraft: (draft: string) => void): {
  intro: OpenIntro | null;
  showIntro: (request?: IntroRequest) => Promise<boolean>;
  reportIntroShown: (shown: boolean) => void;
  closeIntro: (draft?: string) => void;
} {
  const userId = useSession().data?.user.id ?? null;
  const [intro, setIntro] = useState<OpenIntro | null>(null);
  const opened = useRef(0);
  const awaitingShown = useRef<((shown: boolean) => void) | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);

  const reportIntroShown = useCallback((shown: boolean) => {
    awaitingShown.current?.(shown);
    awaitingShown.current = null;
  }, []);

  const showIntro = useCallback(
    (request: IntroRequest = WELCOME) =>
      new Promise<boolean>((resolve) => {
        const focused = document.activeElement;
        if (
          returnFocus.current === null &&
          focused instanceof HTMLElement &&
          focused !== document.body
        ) {
          returnFocus.current = focused;
        }
        awaitingShown.current?.(false);
        awaitingShown.current = resolve;
        opened.current += 1;
        setIntro({ request, key: opened.current });
      }),
    [],
  );

  useEffect(() => {
    if (userId && !introSeen(userId)) void showIntro(WELCOME);
  }, [userId, showIntro]);

  useEffect(() => {
    if (intro !== null) return;
    const focused = returnFocus.current;
    returnFocus.current = null;
    if (focused?.isConnected && document.activeElement === document.body) {
      focused.focus({ preventScroll: true });
    }
  }, [intro]);

  const closeIntro = useCallback(
    (draft?: string) => {
      reportIntroShown(false);
      setIntro(null);
      if (draft) onDraft(draft);
    },
    [reportIntroShown, onDraft],
  );

  return { intro, showIntro, reportIntroShown, closeIntro };
}

interface OpenIntro {
  request: IntroRequest;
  key: number;
}

function viewOf(path: string): string {
  return path.split("/")[1] ?? "";
}

interface Keyed<T> {
  pathname: string;
  value: T;
}

const NO_HANDLE: ShellRouteHandle = {};

const NO_HOSTS: WorkHeadHosts = { title: null, actions: null, tabs: null, dialog: null };

const COMPOSER_PICKUP_MS = 10_000;

const WELCOME: IntroRequest = { from: "welcome" };

const CreateClientPanel = lazy(async () => ({
  default: (await import("@/features/clients/components/CreateClientPanel")).CreateClientPanel,
}));

const IntroOverlay = lazy(async () => ({
  default: (await import("@/features/intro/components/IntroOverlay")).IntroOverlay,
}));
